/** @jsx jsx */
import React from 'react';
import noop from 'lodash/noop';
import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { InlineDueDatePicker } from '@atlassian/jira-inline-due-date-picker/src/InlineDueDatePicker.tsx';
import { InlineAssigneePicker } from '@atlassian/jira-inline-assignee-picker/src/InlineAssigneePicker.tsx';
import type { UserValue } from '@atlassian/jira-issue-field-assignee/src/common/types.tsx';
import type { ConfigureSource } from '@atlassian/jira-issue-view-common-types/src/inline-create-fields-type.tsx';

export type DueDate = Date | null;
export type InlineCardCreateAdditionalFieldsProps = {
	projectKey: string;
	shouldShowNameLabel?: boolean;
	isCompactDueDatePicker?: boolean;
	onAssigneeUpdate?: (updatedValue: UserValue) => void;
	defaultAssignee?: UserValue;
	onDateSelected?: (date: DueDate) => void;
	defaultDueDate?: DueDate;
	source?: ConfigureSource;
	shouldShowAssigneePicker?: boolean;
	shouldShowDueDatePicker?: boolean;
	onUpdatingAssigneePicker?: (isUpdatingAssigneePicker: boolean) => void;
	onUpdatingDueDatePicker?: (isUpdatingDueDatePicker: boolean) => void;
	fullWidth?: boolean;
	onClick?: () => void;
	shouldRenderToParent?: boolean;
	strategy?: 'absolute' | 'fixed';
	// Callback to close parent form when clicked on 'ESCAPE' key
	onClose?: () => void;
	shouldFocusOnAssigneeTrigger?: boolean;
};

export const InlineCardCreateAdditionalFields = ({
	projectKey,
	shouldShowNameLabel,
	isCompactDueDatePicker,
	onAssigneeUpdate,
	defaultAssignee,
	onDateSelected,
	defaultDueDate,
	source,
	shouldShowAssigneePicker = false,
	shouldShowDueDatePicker = false,
	onUpdatingAssigneePicker,
	onUpdatingDueDatePicker,
	fullWidth = true,
	onClick,
	shouldRenderToParent = false,
	strategy = 'absolute',
	onClose = noop,
	shouldFocusOnAssigneeTrigger = true,
}: InlineCardCreateAdditionalFieldsProps) => {
	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<div role="presentation" onClick={onClick} css={[styles, !fullWidth && notFullWidthStyles]}>
			{shouldShowDueDatePicker && (
				<InlineDueDatePicker
					onDateSelected={onDateSelected}
					defaultDueDate={defaultDueDate}
					isCompactVersion={isCompactDueDatePicker}
					onUpdatingDueDate={onUpdatingDueDatePicker}
					source={source}
					shouldRenderToParent={shouldRenderToParent}
					onClose={onClose}
				/>
			)}
			{shouldShowAssigneePicker && (
				<InlineAssigneePicker
					projectKey={projectKey}
					shouldShowNameLabel={shouldShowNameLabel}
					onAssigneeUpdate={onAssigneeUpdate}
					defaultAssignee={defaultAssignee}
					source={source}
					onUpdatingAssigneePicker={onUpdatingAssigneePicker}
					strategy={strategy}
					onClose={onClose}
					shouldFocusTrigger={shouldFocusOnAssigneeTrigger}
				/>
			)}
		</div>
	);
};

const styles = css({
	display: 'flex',
	gap: token('space.075'),
	alignItems: 'center',
	justifyContent: 'start',
	flexDirection: 'row',
	flexGrow: 1,
});

const notFullWidthStyles = css({
	flexGrow: 0,
});

export default InlineCardCreateAdditionalFields;
